/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useMemo, useState} from 'react';
import {I18nextProvider} from 'react-i18next';
import {QueryClientProvider} from 'react-query';
import {ThemeProvider} from '@material-ui/core/styles';
// the router is read here as RouteDefinitions exports no router helper: D38
// puts the dls Feature Flag back on the URL once the router is ready, after
// Next.js has rewritten the URL as the page loaded ...
// eslint-disable-next-line no-restricted-imports
import {useRouter} from 'next/router';
import PageViewportMeta from '@ion/applications/src/components/PageViewportMeta';
import {isCrossEnvironment} from '@ion/applications/src/env/env';
import EnvironmentNotification from '@ion/applications/src/env/EnvironmentNotification';
import useRouteInitializer from '@ion/applications/src/hooks/useRouteInitializer';
import {ApplicationPage} from '@ion/applications/src/layout/types';
import {NetworkStatusPrompt} from '@ion/applications/src/prompts/NetworkStatusPrompt';
import {getFeatureFlags} from '@ion/applications/src/routes/RouteUtils';
import StoreProvider from '@ion/applications/src/store/StoreProvider';
import {launchReduxDevTools} from '@ion/applications/src/store/tools';
import {includeDatadog, includeDynatrace, isDevMode} from '@ion/applications/src/utils/env';
import Datadog from '@ion/core/src/analytics/Datadog';
import Dynatrace from '@ion/core/src/analytics/Dynatrace';
import Pendo from '@ion/core/src/analytics/Pendo';
import {get, getEnvironment, isEUProduction} from '@ion/core/src/environment/environment';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import i18n, {initializeLanguageAsync, localeHMR} from '@ion/core/src/locale/locale';
import {enable, error, info} from '@ion/core/src/logger/logger';
import {setArcGISConfig} from '@ion/core/src/services/ArcGISUtils';
import {setConnectConfig} from '@ion/core/src/services/ConnectUtils';
import {setHereConfig} from '@ion/core/src/services/HereUtils';
import Sisense from '@ion/core/src/services/Sisense';
import {ContentLoader} from '@ion/dls/src/components/loading/ContentLoader';
import {registerGrid} from '@ion/dls/src/grid/IonGridLicence';
import {ErrorBoundary} from '@ion/dls/src/notifications/ErrorBoundary';
import {FontLoader} from '@ion/dls/src/theme/resellers/fonts/FontLoader';
import {
  initResellerDevTools
} from '@ion/dls/src/theme/resellers/ResellerDevTools';
import {
  getTranslationsFilename,
  isReseller,
  isResellerHost,
  Reseller
} from '@ion/dls/src/theme/resellers/Resellers';
import SASSThemeResolver from '@ion/dls/src/theme/SASSThemeResolver';
import {bindThemeModeAttribute} from '@ion/dls/src/theme/ThemeModeAttribute';
import {setDarkModeEnabled} from '@ion/dls/src/theme/ThemeStore';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getQueryClient} from '@ion/query/src/config/client';
import {DevTools} from '@ion/query/src/tools/DevTools';
import {setGraphQLConfig} from '@ion/services/src/graphql/GraphQLConfig';
import {initializeRouter, isDebug, setQuery} from '@/routes/RouteDefinitions';
import rootReducer from '@/store/reducers';
import loadConfig from '../env/env';
import {useApplicationTheme} from '../hooks/useApplicationTheme';
import Layout from '../layout/Layout';
import FeatureFlags from '../security/FeatureFlags';
import {getThemeFeatureFlagLocation, initializeThemeAccess} from '../security/themeAccess';
import {
  getAPMEnablement,
  initializeAPM,
  initializeAPMClients
} from '../telemetry/apm';
import '@ion/applications/src/styles/index.scss';
import '@ion/dls/src/maps/styles/esri-attribution.scss';
import '@ion/dls/src/theme/theme-dark-vars.scss';

const isDev = isDevMode();

// the New Theme Toggle and the theme are settled from the query string, or
// the access the browser remembers, before the first render, so the login
// page already shows the theme the user will have - held until the page is
// reloaded ...
if (typeof window !== 'undefined') {
  initializeThemeAccess(getFeatureFlags('features'));
}

if (isDev) {
  initResellerDevTools();
}

// opts in to dark mode, except on a reseller host (which reads the simulated
// reseller set above), and binds the mode to <html>, both before first render
// so a stored dark mode is applied with no flash of light ...
setDarkModeEnabled(!isResellerHost());
bindThemeModeAttribute();

/**
 * Defines the root iOn Application, this component wraps all routes and injects
 * all providers, including i18Next and Material UI.
 */
function ION({Component, pageProps}: ApplicationPage) {
  const [initialized, setInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  const router          = useRouter();
  const theme: DLSTheme = useApplicationTheme();
  const isMounted       = useHasMounted();
  const i18nMemo        = useMemo(() => i18n, [i18n]);
  const pagePropsMemo   = useMemo(() => pageProps, [pageProps]);
  const layoutProps     = useMemo(() => Component.getLayoutProps?.() || {}, [Component]);

  // Used for compatibility between App Router and Pages Router
  useRouteInitializer();

  useEffect(() => {
    FeatureFlags.applyFeatureFlags(getFeatureFlags('features'));
  }, []);

  // puts the remembered `dls` Feature Flag back on the address bar once the
  // router is ready, after Next.js has rewritten the URL as the page loaded ...
  useEffect(() => {
    const restored = router.isReady ? getThemeFeatureFlagLocation() : undefined;
    if (restored) {
      const {pathname, search, hash} = restored;
      router.replace(router.pathname + search, `${pathname}${search}${hash}`, {shallow: true, scroll: false})
        .then(function() {
          setQuery();
        })
        .catch(function(err) {
          error(`Unable to restore the dls feature flag to the URL, ${err}`);
        });
    }
  }, [router.isReady]);

  useEffect(() => {
    // Defines an async utility to initialize language
    async function initializeLanguage() {
      await initializeLanguageAsync(null, getTranslationsFilename());
      const config = await loadConfig();
      if (!isDev && !isEUProduction()) {
        Pendo.injectScript(config?.pendoAPIKey, globalThis);
      }
      if (!isDev && includeDynatrace()) {
        Dynatrace.initialize();
      }
      const enableDatadog = (
        !isDev && includeDatadog() && !isCrossEnvironment() && !isReseller(false)
      );

      if (enableDatadog) {
        Datadog.initialize({
          service : 'ion-suite',
          env     : getEnvironment(),
          version : process.env.NEXT_PUBLIC_APP_VERSION
        });
      }
      setGraphQLConfig({
        serviceURL : get('graphQLServiceURL'),
        appKey     : get('appKey')
      });
      initializeAPMClients();
      Sisense.injectFrameScript();
      setConnectConfig(config);
      setArcGISConfig(config);
      setHereConfig(config);
      setInitialized(true);
      registerGrid(config);
      isDev && localeHMR();
      isDev && launchReduxDevTools();
    };

    if (isMounted && !initialized && !isInitializing) {
      setIsInitializing(true);
      const {enableAPMDebugOnly} = getAPMEnablement();
      enable(isDebug() || isDev || enableAPMDebugOnly);
      initializeAPM();
      initializeRouter();
      info('Initializing iOn Suite Application ...');
      initializeLanguage();
    }
  }, [isMounted, initialized, isInitializing]);

  return (
    <>
      <PageViewportMeta />
      <FontLoader />
      <SASSThemeResolver />
      {(theme && initialized) && (
        <I18nextProvider i18n={i18nMemo}>
          <ThemeProvider theme={theme}>
            <QueryClientProvider client={getQueryClient()}>
              <ErrorBoundary>
                <StoreProvider rootReducer={rootReducer}>
                  {!layoutProps.omit ? (
                    <Layout {...layoutProps}>
                      <Component {...pagePropsMemo} />
                    </Layout>
                  ) : (
                    <Component {...pagePropsMemo}/>
                  )}
                </StoreProvider>
              </ErrorBoundary>
              <DevTools />
            </QueryClientProvider>
          </ThemeProvider>
        </I18nextProvider>
      )}
      {(theme && isMounted && !initialized) && (
        <ThemeProvider theme={theme}>
          <ContentLoader width='60px' height='60px' fullPage />
        </ThemeProvider>
      )}
      <NetworkStatusPrompt />
      <EnvironmentNotification app='iOn Suite' />
    </>
  );
};
export default React.memo(ION);
